import type { PersonalProject } from '@portfolio/shared';
import type { JSX } from 'react';

import { Card } from '@/components/ui/Card';
import { Chip, ChipList } from '@/components/ui/Chip';
import { ExternalLinkIcon } from '@/components/ui/icons';

interface ProjectCardProps {
  project: PersonalProject;
  headingLevel?: 'h3' | 'h4';
}

/**
 * The whole card is a link to the live site via a stretched anchor, so pointer
 * users get a large target while keyboard users tab to exactly one control.
 */
export function ProjectCard({
  project,
  headingLevel: Heading = 'h3',
}: ProjectCardProps): JSX.Element {
  return (
    <Card as="li" interactive className="relative flex flex-col">
      <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
        <Chip tone="accent">Personal project</Chip>
        <span className="wrap-anywhere font-mono">{project.displayUrl}</span>
      </div>

      <Heading className="mt-4 text-xl">
        <a
          href={project.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-text no-underline after:absolute after:inset-0 after:content-[''] hover:underline"
        >
          {project.name}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Heading>

      <p className="mt-1 text-sm text-accent">{project.tagline}</p>
      <p className="mt-4 text-sm text-muted">{project.summary}</p>

      <ul className="mt-5 flex flex-col gap-2 border-t border-line pt-4 text-sm text-muted">
        {project.highlights.map((highlight) => (
          <li key={highlight} className="flex gap-2">
            <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
            {highlight}
          </li>
        ))}
      </ul>

      <p className="mt-5 flex-1 text-sm text-subtle">{project.outcome}</p>

      <ChipList items={project.focus} label={`${project.name} focus areas`} className="mt-5" />

      <p
        aria-hidden="true"
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent"
      >
        Visit {project.displayUrl}
        <ExternalLinkIcon width={16} height={16} />
      </p>
    </Card>
  );
}
